import { Injectable, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, catchError, map, throwError } from 'rxjs';
import { ApiService } from './api.service';
import { StorageService } from './storage.service';
import { LoginDto, LoginResponseDto, UserResponseDto } from '../interfaces/auth.interface';

@Injectable({
  providedIn: 'root'
})
export class AuthService extends ApiService {
  private storageService = inject(StorageService);
  private router = inject(Router);

  private readonly AUTH_TOKEN_KEY = 'auth_token';
  private readonly AUTH_USER_KEY = 'auth_user';
  private readonly AUTH_EXPIRES_AT_KEY = 'auth_expires_at';

  private currentUser = signal<UserResponseDto | null>(null);
  private isAuth = signal<boolean>(false);

  constructor() {
    super();
    this.loadAuthFromStorage();
  }

  login(credentials: LoginDto): Observable<LoginResponseDto> {
    return this.post<LoginResponseDto>('auth/login', credentials).pipe(
      map(response => {
        this.setAuth(response);
        return response;
      }),
      catchError(error => {
        return throwError(() => error);
      })
    );
  }

  logout(redirectToLogin = true): void {
    this.clearSession();
    if (redirectToLogin) {
      this.router.navigate(['/login']);
    }
  }

  isAuthenticated(): boolean {
    if (!this.isAuth()) {
      return false;
    }

    const expiresAt = this.storageService.getItem(this.AUTH_EXPIRES_AT_KEY);
    if (expiresAt) {
      const expirationDate = new Date(expiresAt);
      if (expirationDate <= new Date()) {
        this.clearSession();
        return false;
      }
    }

    return true;
  }

  private clearSession(): void {
    this.storageService.removeItem(this.AUTH_TOKEN_KEY);
    this.storageService.removeItem(this.AUTH_USER_KEY);
    this.storageService.removeItem(this.AUTH_EXPIRES_AT_KEY);
    this.currentUser.set(null);
    this.isAuth.set(false);
  }

  getToken(): string | null {
    return this.storageService.getItem(this.AUTH_TOKEN_KEY);
  }

  getCurrentUser(): UserResponseDto | null {
    return this.currentUser();
  }

  currentUserSignal() {
    return this.currentUser;
  }

  refreshCurrentUser(): void {
    // Recargar el usuario desde el storage o hacer una nueva petición
    const userStr = this.storageService.getItem(this.AUTH_USER_KEY);
    if (userStr) {
      try {
        const user = JSON.parse(userStr) as UserResponseDto;
        this.currentUser.set(user);
        this.storageService.setItem(this.AUTH_USER_KEY, JSON.stringify(user));
      } catch {
        // Si falla, mantener el usuario actual
      }
    }
  }

  updateCurrentUser(user: any): void {
    // Convertir null a undefined para compatibilidad
    const authUser: UserResponseDto = {
      ...user,
      firstName: user.firstName ?? undefined,
      lastName: user.lastName ?? undefined,
      lastLoginAt: user.lastLoginAt ?? undefined
    };
    this.currentUser.set(authUser);
    this.storageService.setItem(this.AUTH_USER_KEY, JSON.stringify(authUser));
  }

  private setAuth(response: LoginResponseDto): void {
    this.storageService.setItem(this.AUTH_TOKEN_KEY, response.token);
    this.storageService.setItem(this.AUTH_USER_KEY, JSON.stringify(response.user));
    this.storageService.setItem(this.AUTH_EXPIRES_AT_KEY, response.expiresAt);
    this.currentUser.set(response.user);
    this.isAuth.set(true);
  }

  private loadAuthFromStorage(): void {
    const token = this.storageService.getItem(this.AUTH_TOKEN_KEY);
    const userStr = this.storageService.getItem(this.AUTH_USER_KEY);

    if (token && userStr) {
      try {
        const user = JSON.parse(userStr) as UserResponseDto;
        this.currentUser.set(user);
        this.isAuth.set(true);

        if (!this.isAuthenticated()) {
          this.clearSession();
        }
      } catch {
        this.clearSession();
      }
    }
  }
}

